import { useState, useEffect } from "react";
import { useRoute, Link } from "wouter";
import type { CollectionDetail, Project, Collection } from "@/api/client";
import { api } from "@/api/client";
import { ProjectCard } from "@/components/ProjectCard";
import { CollectionCard } from "@/components/CollectionCard";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  Loader2, 
  Folder, 
  Edit2, 
  Plus, 
  X,
  Check,
  FolderPlus,
  FilePlus 
} from "lucide-react";
import { formatDistanceToNow } from "@/lib/utils";

export function CollectionDetailPage() {
  const [, params] = useRoute("/collections/:id");
  const collectionId = params?.id;

  const [collection, setCollection] = useState<CollectionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // Add item modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState<'project' | 'collection'>('project');
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [availableCollections, setAvailableCollections] = useState<Collection[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const fetchCollection = async () => {
    if (!collectionId) return;

    const response = await api.collections.get(collectionId);
    if (response.success && response.data) {
      setCollection(response.data);
      setEditName(response.data.name);
      setEditDescription(response.data.description || "");
    } else {
      setError(response.error || "Failed to load collection");
    }
    setLoading(false);
  };

  const fetchAvailableItems = async () => {
    setLoadingItems(true);
    
    // Fetch all projects and collections
    const [projectsRes, collectionsRes] = await Promise.all([
      api.projects.list(),
      api.collections.list(),
    ]);

    if (projectsRes.success && projectsRes.data) {
      // Filter out projects already in this collection
      const existingProjectIds = collection?.items
        .filter(i => i.item.itemType === 'project')
        .map(i => i.item.itemId) || [];
      setAvailableProjects(
        projectsRes.data.filter(p => !existingProjectIds.includes(p.id))
      );
    }

    if (collectionsRes.success && collectionsRes.data) {
      // Filter out current collection and collections already in this collection
      const existingCollectionIds = collection?.items
        .filter(i => i.item.itemType === 'collection')
        .map(i => i.item.itemId) || [];
      setAvailableCollections(
        collectionsRes.data.filter(
          c => c.id !== collectionId && !existingCollectionIds.includes(c.id)
        )
      );
    }

    setLoadingItems(false);
  };

  useEffect(() => {
    fetchCollection();
  }, [collectionId]);

  useEffect(() => {
    if (showAddModal) {
      fetchAvailableItems();
    }
  }, [showAddModal]);

  const handleSave = async () => {
    if (!collectionId || !editName.trim()) return;
    
    setSaving(true);
    const response = await api.collections.update(collectionId, {
      name: editName.trim(),
      description: editDescription.trim() || undefined,
    });
    
    if (response.success) {
      await fetchCollection();
      setIsEditing(false);
    } else {
      alert(response.error || "Failed to update collection");
    }
    setSaving(false);
  };

  const handleAddItem = async (itemType: 'project' | 'collection', itemId: string) => {
    if (!collectionId) return;
    
    const response = await api.collections.addItem(collectionId, { itemType, itemId });
    if (response.success) {
      await fetchCollection();
      await fetchAvailableItems();
    } else {
      alert(response.error || "Failed to add item");
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!collectionId) return;
    if (!confirm("确定要从合集中移除此项目吗？")) return;
    
    const response = await api.collections.removeItem(collectionId, itemId);
    if (response.success) {
      await fetchCollection();
    } else {
      alert(response.error || "Failed to remove item");
    }
  };

  const handleDeleteProject = async (id: string) => {
    if (!confirm("确定要删除这个项目吗？")) return;

    const response = await api.projects.delete(id);
    if (response.success) {
      await fetchCollection();
    } else {
      alert(response.error || "Failed to delete project");
    }
  };

  const handleDeleteCollection = async (id: string) => {
    if (!confirm("确定要删除这个合集吗？")) return;

    const response = await api.collections.delete(id);
    if (response.success) {
      await fetchCollection();
    } else {
      alert(response.error || "Failed to delete collection");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">{error || "合集不存在"}</p>
        <Link href="/">
          <Button>返回首页</Button>
        </Link>
      </div>
    );
  }

  const projectItems = collection.items.filter(i => i.item.itemType === 'project');
  const collectionItems = collection.items.filter(i => i.item.itemType === 'collection');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-1.5 text-xl font-bold bg-transparent border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="合集名称"
                />
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm bg-transparent border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="合集描述（可选）"
                />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-violet-500/10 flex items-center justify-center">
                    <Folder className="w-4 h-4 text-violet-500" />
                  </div>
                  <h1 className="text-xl font-bold text-foreground">{collection.name}</h1>
                </div>
                {collection.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {collection.description}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  创建于 {formatDistanceToNow(new Date(collection.createdAt))}
                </p>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setIsEditing(false);
                    setEditName(collection.name);
                    setEditDescription(collection.description || "");
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  onClick={handleSave}
                  disabled={saving || !editName.trim()}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={() => setShowAddModal(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  添加已有项目
                </Button>
                <Link href={`/create?collectionId=${collectionId}`}>
                  <Button className="gap-2">
                    <FilePlus className="w-4 h-4" />
                    创建新项目
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Nested Collections */}
        {collectionItems.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground">
                子合集
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({collectionItems.length})
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {collectionItems.map(({ item, data }) => (
                <div key={item.id} className="relative group">
                  <CollectionCard
                    collection={data as Collection}
                    onDelete={handleDeleteCollection}
                  />
                  <button
                    onClick={() => handleRemoveItem(item.itemId)}
                    className="absolute top-2 right-2 p-1.5 bg-background/80 backdrop-blur rounded-lg text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                    title="从合集中移除"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Projects */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-foreground">
              项目
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({projectItems.length})
              </span>
            </h2>
          </div>
          
          {projectItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Folder className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>合集中还没有项目</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowAddModal(true)}
              >
                添加项目
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projectItems.map(({ item, data }) => (
                <div key={item.id} className="relative group">
                  <ProjectCard
                    project={data as Project}
                    onDelete={handleDeleteProject}
                  />
                  <button
                    onClick={() => handleRemoveItem(item.itemId)}
                    className="absolute top-2 right-2 p-1.5 bg-background/80 backdrop-blur rounded-lg text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                    title="从合集中移除"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold">添加到合集</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex border-b border-border">
              <button
                onClick={() => setAddType('project')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  addType === 'project'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                项目 ({availableProjects.length})
              </button>
              <button
                onClick={() => setAddType('collection')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  addType === 'collection'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                合集 ({availableCollections.length})
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {loadingItems ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : addType === 'project' ? (
                availableProjects.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <p>没有可添加的项目</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableProjects.map((project) => (
                      <div
                        key={project.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{project.name}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {project.inputText}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAddItem('project', project.id)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                availableCollections.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <p>没有可添加的合集</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableCollections.map((col) => (
                      <div
                        key={col.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-violet-500/10 flex items-center justify-center flex-shrink-0">
                            <FolderPlus className="w-4 h-4 text-violet-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{col.name}</p>
                            {col.description && (
                              <p className="text-sm text-muted-foreground truncate">
                                {col.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAddItem('collection', col.id)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
